export function groupBy(key, array) {
    return Array.from(array).reduce((obj, element, index, array) => {
        let group =
            element[
            typeof key === 'function' ? key(element, index, array) : key
            ];
        if (!obj[group]) obj[group] = [element];
        else obj[group].push(element);
        return obj;
    }, {});
}

export function deepAssign(target = {}, ...sources) {
    for (let source of sources) {
        for (let key of Object.keys(source)) {
            target[key] =
                source[key]?.constructor === Object
                    ? deepAssign(target[key], source[key])
                    : source[key];
        }
    }
    return target;
}

export function isNumeric(object) {
    return parseFloat(object) === Number(object);
}

export function divideOrZero(dividend, divisor) {
    return divisor ? dividend / divisor : 0;
}

export function formatValue(
    value,
    options = {},
    locales = ['de-DE', 'en-US', 'de', 'en']
) {
    let formatter = new Intl.NumberFormat(locales, options);
    return formatter.format(value);
}

export function formatDate(value, options = {}, locales = ['de-DE', 'en-US', 'de', 'en']) {
    let date = new Date(value);
    let formatter = new Intl.DateTimeFormat(locales, options);
    return date.toJSON() ? formatter.format(date) : value;
}

export function formatDateByGranularity(
    value,
    granularity,
    locales = ['de-DE', 'en-US', 'de', 'en']
) {
    switch (granularity) {
        case 'year':
            return formatDate(value, { year: 'numeric' }, locales);
        case 'month':
            return formatDate(value, { month: 'short' }, locales);
        case 'week':
            return value?.toString().replace(/.*(W\d{1,2}).*/, '$1');
        default:
            return formatDate(
                value,
                { day: 'numeric', month: 'numeric' },
                locales
            );
    }
}

export function localISODate(value = new Date()) {
    let date = new Date(value);
    return (
        date.toJSON() &&
        [
            date.getFullYear(),
            (date.getMonth() + 1).toString().padStart(2, '0'),
            date.getDate().toString().padStart(2, '0'),
        ].join('-')
    );
}

export function localISOTime(value = new Date()) {
    let date = new Date(value);
    return date.toJSON() && date.toTimeString().slice(0, 8);
}

export function localISOString(value = new Date()) {
    let date = new Date(value);
    return (
        date.toJSON() &&
        localISODate(date) +
        'T' +
        date.toTimeString().slice(0, 17).replace(' GMT', '')
    );
}

// Mirrors Reservation::TIME_ZONE. The parking area's wall clock is what the visitor books,
// whatever timezone they happen to be browsing from.
export const PARKING_TIME_ZONE = 'Europe/Vienna';

export function parkingWallClockNow() {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: PARKING_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(new Date());

    const part = (type) =>
        Number(parts.find((entry) => entry.type === type).value);

    return new Date(
        part('year'),
        part('month') - 1,
        part('day'),
        part('hour'),
        part('minute')
    );
}

// 'YYYY-MM-DDTHH:mm' with no offset, so the backend reads the picked time literally as a
// time at the parking area instead of converting it out of the visitor's timezone.
export function toWallClockString(value = new Date()) {
    let date = new Date(value);
    return (
        date.toJSON() &&
        localISODate(date) + 'T' + date.toTimeString().slice(0, 5)
    );
}

export function queryParams() {
    return typeof location !== 'undefined'
        ? Object.fromEntries(new URLSearchParams(location?.search))
        : {};
}

export function debounce(func, wait = 250) {
    clearTimeout(func._timeout);
    func._timeout = setTimeout(func, wait);
}

export function compare(a, b) {
    return a > b ? 1 : a < b ? -1 : 0; // cf. the 'spaceship' operator ( <=> ) in other languages
}

export function unique(array) {
    return Array.from(new Set(array));
}

export async function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
